/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListShadowMCPInventoryRequest,
  ListShadowMCPInventorySecurity,
} from "../models/operations/listshadowmcpinventory.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildShadowMCPInventoryQuery,
  prefetchShadowMCPInventory,
  queryKeyShadowMCPInventory,
  ShadowMCPInventoryQueryData,
} from "./shadowMCPInventory.core.js";
export {
  buildShadowMCPInventoryQuery,
  prefetchShadowMCPInventory,
  queryKeyShadowMCPInventory,
  type ShadowMCPInventoryQueryData,
};

export type ShadowMCPInventoryQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listShadowMCPInventory access
 *
 * @remarks
 * List project-scoped Shadow MCP server inventory composed from observed URLs, telemetry usage, and policy-bypass state. Requires an authenticated session authorized for org:admin on the active organization.
 */
export function useShadowMCPInventory(
  request: ListShadowMCPInventoryRequest,
  security?: ListShadowMCPInventorySecurity | undefined,
  options?: QueryHookOptions<
    ShadowMCPInventoryQueryData,
    ShadowMCPInventoryQueryError
  >,
): UseQueryResult<ShadowMCPInventoryQueryData, ShadowMCPInventoryQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildShadowMCPInventoryQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listShadowMCPInventory access
 *
 * @remarks
 * List project-scoped Shadow MCP server inventory composed from observed URLs, telemetry usage, and policy-bypass state. Requires an authenticated session authorized for org:admin on the active organization.
 */
export function useShadowMCPInventorySuspense(
  request: ListShadowMCPInventoryRequest,
  security?: ListShadowMCPInventorySecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ShadowMCPInventoryQueryData,
    ShadowMCPInventoryQueryError
  >,
): UseSuspenseQueryResult<
  ShadowMCPInventoryQueryData,
  ShadowMCPInventoryQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildShadowMCPInventoryQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setShadowMCPInventoryData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      projectId: string;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ShadowMCPInventoryQueryData,
): ShadowMCPInventoryQueryData | undefined {
  const key = queryKeyShadowMCPInventory(...queryKeyBase);

  return client.setQueryData<ShadowMCPInventoryQueryData>(key, data);
}

export function invalidateShadowMCPInventory(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      projectId: string;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listShadowMCPInventory",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllShadowMCPInventory(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listShadowMCPInventory"],
  });
}
